import type { UpdateQuery } from "rxdb"
import type { useDatabase } from "./index"
import type {
  BookDocType,
  CollectionDocType,
  LinkDocType,
  TagsDocType,
} from "@oboku/shared"

type Database = NonNullable<ReturnType<typeof useDatabase>["db"]>

export const applyHooks = (db: Database) => {
  db.book.postSave(async (data) => {
    const tagsFromWhichToRemoveBook = await db.tag
      .find({
        selector: {
          books: {
            $in: [data._id],
          },
          _id: {
            $nin: data.tags,
          },
        },
      })
      .exec()

    // @todo bulk
    await Promise.all(
      tagsFromWhichToRemoveBook.map(async (tag) => {
        await tag.incrementalUpdate({
          $pullAll: {
            books: [data._id],
          },
        })
      }),
    )

    // Update all the tags that are referenced by this book but are
    // not yet linked
    const tagsFromWhichToAddBook = await db.tag
      .find({
        selector: {
          books: {
            // if at least one of the books is data._id it will work.
            // be careful with $nin
            $nin: [data._id],
          },
          _id: {
            $in: data.tags,
          },
        },
      })
      .exec()

    // @todo bulk
    await Promise.all(
      tagsFromWhichToAddBook.map(async (tag) => {
        await tag.incrementalUpdate({
          $push: {
            books: data._id,
          },
        })
      }),
    )
  }, false)

  db.book.postRemove(async (data) => {
    /**
     * When a book is removed, detach it from all tags
     * that contains its reference
     */
    const tagsContainingBook = await db.tag
      .find({
        selector: {
          books: {
            $in: [data._id],
          },
        },
      })
      .exec()

    await Promise.all(
      tagsContainingBook.map(async (tag) => {
        await tag.incrementalUpdate({
          $pullAll: {
            books: [data._id],
          },
        } satisfies UpdateQuery<TagsDocType>)
      }),
    )

    // detach all collections to this book
    const collectionsContainingBook = await db.obokucollection
      .find({
        selector: {
          books: {
            $in: [data._id],
          },
        },
      })
      .exec()

    await Promise.all(
      collectionsContainingBook.map(async (rxDocument) =>
        rxDocument.incrementalUpdate({
          $pullAll: {
            books: [data._id],
          },
        } satisfies UpdateQuery<CollectionDocType>),
      ),
    )

    /**
     * Remove any link attached to that book
     */
    await db.link.find({ selector: { book: data._id } }).incrementalRemove()
  }, false)

  db.book.postInsert(async (data) => {
    /**
     * When a book is added, make sure to attach it to any links
     */
    await db.link
      .find({
        selector: {
          _id: {
            $in: data.links,
          },
        },
      })
      .update({
        $set: {
          book: data._id,
        },
      } satisfies UpdateQuery<LinkDocType>)
  }, false)

  db.tag.postRemove(async (data) => {
    const booksFromWhichToRemoveTag = await db.book
      .find({
        selector: {
          tags: {
            $in: [data._id],
          },
        },
      })
      .exec()

    // @todo bulk
    await Promise.all(
      booksFromWhichToRemoveTag.map(async (book) => {
        await book.incrementalUpdate({
          $pullAll: {
            tags: [data._id],
          },
        })
      }),
    )
  }, false)

  db.obokucollection.postRemove(async (data) => {
    // remove any book that were attached to this collection
    const booksWithCollectionAttached = await db.book
      .find({
        selector: {
          collections: {
            $in: [data._id],
          },
        },
      })
      .exec()
    await Promise.all(
      booksWithCollectionAttached.map(async (book) =>
        book.incrementalUpdate({
          $pullAll: {
            collections: [data._id],
          },
        } satisfies UpdateQuery<BookDocType>),
      ),
    )
  }, false)
}
